import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, CheckCircle, XCircle, Clock, 
  Eye, ArrowLeft, Search, Image, Upload, Tags, GripVertical, FileText, Download,
  Video, Code, Link
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useDropzone } from 'react-dropzone';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = ['Design', 'Technology', 'Life', 'Religion', 'Others'];

const AdminBlog = () => {
  const { user, isAdmin, canAccessTool } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'Design',
    featuredImage: '',
    referenceDocuments: [],
    videoUrl: '',
    contentType: 'text', // 'text' or 'html'
    status: 'draft'
  });

  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: ''
  });

  // Check if user can manage categories
  const canManageCategories = isAdmin || user?.permissions?.blog?.manageCategories;

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isAdmin && !canAccessTool('blog-manager')) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, isAdmin]);

  const fetchData = async () => {
    try {
      const [postsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/blog/all`),
        axios.get(`${API}/blog/categories/list`)
      ]);
      setPosts(postsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch blog data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/all`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories/list`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Get category names for the select dropdown
  const categoryNames = categories.length > 0 
    ? categories.map(c => c.name) 
    : DEFAULT_CATEGORIES;

  const filteredPosts = posts.filter(post => {
    const matchesFilter = filter === 'all' || post.status === filter;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Image upload handler
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    setUploading(true);
    const file = acceptedFiles[0];
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    
    try {
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({
        ...prev,
        featuredImage: response.data.url
      }));
      
      toast({
        title: "Image Uploaded",
        description: "Featured image uploaded successfully"
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    multiple: false
  });

  const removeImage = () => {
    setFormData(prev => ({ ...prev, featuredImage: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const slug = formData.title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    const postData = {
      ...formData,
      slug
    };

    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost._id}`, postData);
        toast({
          title: "Success",
          description: "Blog post updated successfully"
        });
      } else {
        await axios.post(`${API}/blog`, postData);
        toast({
          title: "Success",
          description: "Blog post created successfully"
        });
      }
      
      setDialogOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to save post",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      category: post.category,
      featuredImage: post.featuredImage || '',
      referenceDocuments: post.referenceDocuments || [],
      videoUrl: post.videoUrl || '',
      contentType: post.contentType || 'text',
      status: post.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/blog/${id}`);
      toast({
        title: "Success",
        description: "Post deleted successfully"
      });
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive"
      });
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      if (newStatus === 'approved') {
        await axios.put(`${API}/blog/${id}/approve`);
      } else if (newStatus === 'rejected') {
        await axios.put(`${API}/blog/${id}/reject`);
      } else {
        await axios.put(`${API}/blog/${id}`, { status: newStatus });
      }
      toast({
        title: "Success",
        description: `Post ${newStatus === 'approved' ? 'approved' : newStatus === 'rejected' ? 'rejected' : 'updated'}`
      });
      fetchPosts();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update post status",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      category: categoryNames[0] || 'Design',
      featuredImage: '',
      referenceDocuments: [],
      videoUrl: '',
      contentType: 'text',
      status: 'draft'
    });
  };

  // Document upload handler
  const handleDocumentUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingDoc(true);
    const uploadedDocs = [];

    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append('document', file);

      try {
        const response = await axios.post(`${API}/upload/document`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedDocs.push({
          name: file.name,
          url: response.data.url,
          type: file.type,
          size: file.size
        });
      } catch (error) {
        console.error('Upload error:', error);
        toast({
          title: "Upload Failed",
          description: `Failed to upload ${file.name}`,
          variant: "destructive"
        });
      }
    }

    if (uploadedDocs.length > 0) {
      setFormData(prev => ({
        ...prev,
        referenceDocuments: [...prev.referenceDocuments, ...uploadedDocs]
      }));
      toast({
        title: "Documents Uploaded",
        description: `${uploadedDocs.length} document(s) uploaded successfully`
      });
    }

    setUploadingDoc(false);
    event.target.value = ''; // Reset input
  };

  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      referenceDocuments: prev.referenceDocuments.filter((_, i) => i !== index)
    }));
  };

  // Category management functions
  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: '', description: '' });
  };

  const handleOpenCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({
        name: category.name,
        description: category.description || ''
      });
    } else {
      resetCategoryForm();
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    
    if (!categoryFormData.name.trim()) {
      toast({
        title: "Error",
        description: "Category name is required",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingCategory?._id) {
        await axios.put(`${API}/blog/categories/${editingCategory._id}`, categoryFormData);
        toast({
          title: "Success",
          description: "Category updated successfully"
        });
      } else {
        await axios.post(`${API}/blog/categories`, categoryFormData);
        toast({
          title: "Success",
          description: "Category created successfully"
        });
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save category",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/blog/categories/${category._id}`);
      toast({
        title: "Success",
        description: "Category deleted successfully"
      });
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete category",
        variant: "destructive"
      });
    }
  };

  const initializeCategories = async () => {
    try {
      await axios.post(`${API}/blog/categories/init`);
      fetchCategories();
      toast({
        title: "Success",
        description: "Categories initialized"
      });
    } catch (error) {
      console.error('Error initializing categories:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-gray-500', icon: Clock, text: 'Draft' },
      pending: { color: 'bg-yellow-500', icon: Clock, text: 'Pending' },
      approved: { color: 'bg-green-500', icon: CheckCircle, text: 'Published' },
      rejected: { color: 'bg-red-500', icon: XCircle, text: 'Rejected' }
    };
    const { color, icon: Icon, text } = config[status] || config.draft;
    return (
      <Badge className={`${color} text-white`}>
        <Icon size={12} className="mr-1" />
        {text}
      </Badge>
    );
  };

  const stats = {
    total: posts.length,
    draft: posts.filter(p => p.status === 'draft').length,
    pending: posts.filter(p => p.status === 'pending').length,
    approved: posts.filter(p => p.status === 'approved').length,
    rejected: posts.filter(p => p.status === 'rejected').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
            >
              <ArrowLeft size={18} className="mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Blog Manager</h1>
              <p className="text-gray-400">Create, edit and manage blog posts</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            {canManageCategories && (
              <Button 
                onClick={() => handleOpenCategoryDialog()}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <Tags className="mr-2" size={18} />
                Categories
              </Button>
            )}
            <Button 
              onClick={() => { resetForm(); setDialogOpen(true); }}
              className="bg-[#F4B400] text-black hover:bg-[#d49f00]"
            >
              <Plus className="mr-2" size={18} />
              New Post
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-white/10 border-white/20 cursor-pointer" onClick={() => setFilter('all')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-gray-400">Total Posts</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-500/20 border-gray-500/40 cursor-pointer" onClick={() => setFilter('draft')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{stats.draft}</p>
              <p className="text-xs text-gray-400">Drafts</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/20 border-yellow-500/40 cursor-pointer" onClick={() => setFilter('pending')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-xs text-gray-400">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/20 border-green-500/40 cursor-pointer" onClick={() => setFilter('approved')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{stats.approved}</p>
              <p className="text-xs text-gray-400">Published</p>
            </CardContent>
          </Card>
          <Card className="bg-red-500/20 border-red-500/40 cursor-pointer" onClick={() => setFilter('rejected')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{stats.rejected}</p>
              <p className="text-xs text-gray-400">Rejected</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'draft', 'pending', 'approved', 'rejected'].map((status) => (
              <Button
                key={status}
                onClick={() => setFilter(status)}
                variant={filter === status ? 'default' : 'outline'}
                size="sm"
                className={filter === status 
                  ? 'bg-[#F4B400] text-black' 
                  : 'border-white/20 text-white hover:bg-white/10'
                }
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Posts List */}
        {filteredPosts.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <p className="text-gray-400 mb-4">No posts found</p>
              <Button 
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-[#F4B400] text-black hover:bg-[#d49f00]"
              >
                <Plus className="mr-2" size={18} />
                Create Your First Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredPosts.map((post) => (
              <Card key={post._id} className="bg-white text-black hover:shadow-lg transition-all">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {post.featuredImage ? (
                      <img 
                        src={post.featuredImage} 
                        alt={post.title}
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Image className="text-gray-400" size={32} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold truncate">{post.title}</h3>
                            {getStatusBadge(post.status)}
                          </div>
                          <p className="text-sm text-gray-500 mb-2 line-clamp-2">{post.excerpt}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-400">
                            <Badge variant="outline">{post.category}</Badge>
                            <span>By: {post.authorId?.name || 'Unknown'}</span>
                            <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 flex-shrink-0">
                          {post.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleStatusChange(post._id, 'approved')}
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleStatusChange(post._id, 'rejected')}
                                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                              >
                                <XCircle size={14} className="mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          {post.status === 'approved' && (
                            <a
                              href={`/blog/${post.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-8 px-3 text-sm border rounded-md hover:bg-gray-100"
                            >
                              <Eye size={14} className="mr-1" />
                              View
                            </a>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(post)}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(post._id)}
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-black">
          <DialogHeader>
            <DialogTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="mt-2"
                placeholder="Enter post title"
              />
            </div>

            <div>
              <Label>Excerpt *</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                required
                rows={2}
                className="mt-2"
                placeholder="Brief description of the post"
              />
            </div>

            <div>
              <Label>Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
                rows={10}
                className="mt-2"
                placeholder="Write your blog post content here..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryNames.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending">Pending Review</SelectItem>
                    <SelectItem value="approved">Published</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Featured Image Upload */}
            <div>
              <Label>Featured Image</Label>
              <div className="mt-2 space-y-3">
                {formData.featuredImage ? (
                  <div className="relative">
                    <img 
                      src={formData.featuredImage} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isDragActive ? 'border-[#F4B400] bg-yellow-50' : 'border-gray-300 hover:border-[#F4B400]'
                    }`}
                  >
                    <input {...getInputProps()} />
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F4B400] mb-2"></div>
                        <p className="text-sm text-gray-500">Uploading...</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                        <p className="text-sm text-gray-500">
                          {isDragActive ? 'Drop image here' : 'Drag & drop an image or click to select'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">Supports: JPG, PNG, WebP</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Video URL */}
            <div>
              <Label className="flex items-center gap-2">
                <Video size={16} className="text-[#F4B400]" />
                Video URL (optional)
              </Label>
              <Input
                value={formData.videoUrl}
                onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                placeholder="YouTube, Vimeo, or direct video URL"
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supports: YouTube, Vimeo, or direct video file URL
              </p>
            </div>

            {/* Content Type Toggle */}
            <div>
              <Label className="flex items-center gap-2">
                <Code size={16} className="text-[#F4B400]" />
                Content Type
              </Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={formData.contentType === 'text' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, contentType: 'text' })}
                  className={formData.contentType === 'text' ? 'bg-[#F4B400] text-black' : ''}
                  size="sm"
                >
                  Plain Text
                </Button>
                <Button
                  type="button"
                  variant={formData.contentType === 'html' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, contentType: 'html' })}
                  className={formData.contentType === 'html' ? 'bg-[#F4B400] text-black' : ''}
                  size="sm"
                >
                  HTML Code
                </Button>
              </div>
              {formData.contentType === 'html' && (
                <p className="text-xs text-orange-600 mt-2">
                  HTML mode allows custom formatting. Use with caution.
                </p>
              )}
            </div>

            {/* Reference Documents */}
            <div>
              <Label>Reference Documents</Label>
              <p className="text-xs text-gray-500 mb-2">Upload PDFs, Word documents, or other reference materials</p>
              <div className="mt-2 space-y-3">
                {/* Document List */}
                {formData.referenceDocuments.length > 0 && (
                  <div className="space-y-2">
                    {formData.referenceDocuments.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                        <div className="flex items-center gap-3">
                          <FileText className="text-[#F4B400]" size={20} />
                          <div>
                            <p className="text-sm font-medium truncate max-w-[200px]">{doc.name}</p>
                            <p className="text-xs text-gray-500">
                              {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : 'Document'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <a 
                            href={doc.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-1 text-blue-500 hover:text-blue-700"
                          >
                            <Download size={16} />
                          </a>
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="p-1 text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                <div className="relative">
                  <input
                    type="file"
                    id="doc-upload"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    onChange={handleDocumentUpload}
                    className="hidden"
                  />
                  <label
                    htmlFor="doc-upload"
                    className={`flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      uploadingDoc ? 'bg-gray-100' : 'hover:border-[#F4B400] hover:bg-yellow-50'
                    }`}
                  >
                    {uploadingDoc ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#F4B400]"></div>
                        <span className="text-sm text-gray-500">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <FileText className="text-gray-400" size={20} />
                        <span className="text-sm text-gray-500">Click to upload documents</span>
                      </>
                    )}
                  </label>
                </div>
                <p className="text-xs text-gray-400">Supports: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT</p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-[#F4B400] text-black hover:bg-[#d49f00]">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Categories Management Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-white text-black">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tags size={20} />
              {editingCategory ? 'Edit Category' : 'Manage Categories'}
            </DialogTitle>
          </DialogHeader>
          
          {!editingCategory ? (
            <div className="space-y-4">
              {/* Category List */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {categories.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <Tags className="mx-auto text-gray-400 mb-2" size={32} />
                    <p className="text-gray-500 text-sm mb-3">No categories yet</p>
                    <Button
                      onClick={initializeCategories}
                      size="sm"
                      variant="outline"
                    >
                      Initialize Default Categories
                    </Button>
                  </div>
                ) : (
                  categories.map((category, index) => (
                    <div 
                      key={category._id || index} 
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="text-gray-400" size={16} />
                        <div>
                          <p className="font-medium">{category.name}</p>
                          {category.description && (
                            <p className="text-xs text-gray-500">{category.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenCategoryDialog(category)}
                        >
                          <Edit size={14} />
                        </Button>
                        {category._id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteCategory(category)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Add New Category Button */}
              <div className="pt-4 border-t">
                <Button
                  onClick={() => {
                    resetCategoryForm();
                    setEditingCategory({ isNew: true });
                  }}
                  className="w-full bg-[#F4B400] text-black hover:bg-[#d49f00]"
                >
                  <Plus className="mr-2" size={18} />
                  Add New Category
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSaveCategory} className="space-y-4">
              <div>
                <Label>Category Name *</Label>
                <Input
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  required
                  className="mt-2"
                  placeholder="e.g., Marketing, Tutorials, News"
                />
              </div>

              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  rows={2}
                  className="mt-2"
                  placeholder="Brief description of this category"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#F4B400] text-black hover:bg-[#d49f00]">
                  {editingCategory?.isNew ? 'Create Category' : 'Save Changes'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setEditingCategory(null)}
                  className="flex-1"
                >
                  Back
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlog;
